import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { BrandMark } from '@/components/app/brand-mark'

import { siteConfig } from '@/constants/site'

type Slide = {
    src: string
    alt: string
    focus: string
    caption: string
}

const SLIDES: Slide[] = [
    {
        src: '/images/login/slide-team.jpg',
        alt: 'Un piccolo team discute davanti al computer in un ufficio luminoso',
        focus: 'object-[50%_62%]',
        caption: 'Gli annunci giusti, ogni mattina.'
    },
    {
        src: '/images/login/slide-azienda.jpg',
        alt: 'Persone al lavoro con il portatile in un ufficio luminoso',
        focus: 'object-[60%_70%]',
        caption: 'Le aziende B2B che cercano il tuo profilo.'
    },
    {
        src: '/images/login/slide-colloquio.jpg',
        alt: 'Un colloquio di lavoro intorno a un tavolo',
        focus: 'object-[50%_45%]',
        caption: 'Le persone giuste a cui scrivere.'
    },
    {
        src: '/images/login/slide-remoto.jpg',
        alt: 'Un ragazzo lavora al portatile in uno studio luminoso',
        focus: 'object-[65%_60%]',
        caption: 'Ogni candidatura, sotto controllo.'
    }
]

const SLIDE_MS = 5500

export const LoginHero: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const [playing, setPlaying] = useState(false)

    // Auto-advance starts after mount and stays off for people who prefer reduced motion
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        setPlaying(true)

        const timer = window.setInterval(() => setActive(current => (current + 1) % SLIDES.length), SLIDE_MS)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <div className='relative h-full w-full overflow-hidden bg-ink'>
            {SLIDES.map((slide, index) => (
                <img
                    key={slide.src}
                    src={slide.src}
                    alt={index === 0 ? slide.alt : ''}
                    aria-hidden={index === 0 ? undefined : true}
                    className={cn(
                        'absolute inset-0 size-full object-cover transition-opacity duration-[1400ms] ease-in-out',
                        slide.focus,
                        index === active ? 'animate-kenburns opacity-100' : 'opacity-0'
                    )}
                    loading={index === 0 ? 'eager' : 'lazy'}
                />
            ))}

            <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/5 to-ink/85' />

            <div className='absolute inset-x-0 top-0 px-6 pt-[max(1.25rem,env(safe-area-inset-top))]'>
                <div aria-hidden='true' className='flex gap-1.5'>
                    {SLIDES.map((slide, index) => (
                        <span key={slide.src} className='h-[3px] flex-1 overflow-hidden rounded-full bg-white/30'>
                            {index < active && <span className='block h-full w-full bg-white' />}

                            {index === active && (
                                <span
                                    key={`${active}-${playing}`}
                                    className={cn('block h-full w-full origin-left bg-white', playing && '[animation:bar-grow_5500ms_linear_both]')}
                                />
                            )}
                        </span>
                    ))}
                </div>

                <p className='mt-4 flex items-center gap-2.5 text-white'>
                    <BrandMark className='size-8 ring-2 ring-white/25' />
                    <span className='font-heading text-[19px] font-semibold tracking-tight'>
                        {siteConfig.name}
                    </span>
                </p>
            </div>

            <div className='absolute inset-x-0 bottom-0 px-6 pb-14'>
                <p className='max-w-[300px] font-heading text-[28px] font-semibold leading-[1.1] tracking-tight text-white'>
                    <span key={active} className='block animate-in duration-700 fade-in slide-in-from-bottom-3'>
                        {SLIDES[active].caption}
                    </span>
                </p>
            </div>
        </div>
    )
}
